import * as React from 'react'
import { router, useForm } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog'
import { Button } from '@/shared/ui/button'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { buttonVariants } from '@/shared/ui/button'
import { t } from '@/shared/lib/i18n'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'
import {
  adminSchedulesHolidaysPath,
  adminSchedulesHolidayPath,
  adminUpdateSystemWorkWeekPath,
} from '@/shared/lib/routes'
import { WeeklyScheduleGrid, ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import type { WebAdminCustomDayScheduleRow, WebAdminHolidayRow } from '@/shared/types/web'

export interface HolidayYearGroup {
  year: number
  holidays: WebAdminHolidayRow[]
}

export const scheduleFromRow = (row: WebAdminCustomDayScheduleRow | null): WeekdaySchedule => {
  if (row == null) {
    return { ...ALL_DAYS_ON }
  }
  return {
    sun: row.sun,
    mon: row.mon,
    tue: row.tue,
    wed: row.wed,
    thu: row.thu,
    fri: row.fri,
    sat: row.sat,
  }
}

export const SystemScheduleForm = (props: {
  initialRow: WebAdminCustomDayScheduleRow | null
  canMutate: boolean
}) => {
  const systemForm = useForm<{ custom_day: WeekdaySchedule }>({
    custom_day: scheduleFromRow(props.initialRow),
  })

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">{t('admin.schedules.systemSchedule.title')}</h3>
      <WeeklyScheduleGrid
        value={systemForm.data.custom_day}
        onChange={(next) => systemForm.setData('custom_day', next)}
        disabled={!props.canMutate}
        showFollowParent={false}
        idPrefix="system"
      />
      {props.canMutate && (
        <Button
          type="button"
          onClick={() =>
            systemForm.patch(adminUpdateSystemWorkWeekPath(), { preserveScroll: true })
          }
          disabled={systemForm.processing}
        >
          {t('admin.schedules.saveWorkWeek')}
        </Button>
      )}
    </div>
  )
}

const RemoveHolidayDialog = (props: { holidayId: number; children: React.ReactNode }) => {
  const handleConfirm = () => {
    router.delete(adminSchedulesHolidayPath(props.holidayId), { preserveScroll: true })
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{props.children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.schedules.customDaysOff.remove')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.schedules.customDaysOff.removeConfirm')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('admin.schedules.customDaysOff.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: 'destructive' })}
            onClick={handleConfirm}
          >
            {t('admin.schedules.customDaysOff.remove')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const CustomDaysOffSection = (props: {
  holidaysByYear: HolidayYearGroup[]
  canMutate: boolean
}) => {
  const addForm = useForm({ holiday: { date: '' } })
  const formErrors = addForm.errors as Record<string, string | string[] | undefined>
  const dateError = formErrors['holiday[date]'] ?? formErrors['date']

  const submitDate = () => {
    addForm.post(adminSchedulesHolidaysPath(), {
      preserveScroll: true,
      onSuccess: () => addForm.setData('holiday', { date: '' }),
    })
  }

  const totalCount = props.holidaysByYear.reduce((sum, g) => sum + g.holidays.length, 0)

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">{t('admin.schedules.customDaysOff.title')}</h3>

      {props.canMutate && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48">
            <DatePickerInput
              value={addForm.data.holiday.date}
              onChange={(val) => addForm.setData('holiday', { date: val })}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={submitDate}
            disabled={addForm.processing || addForm.data.holiday.date === ''}
          >
            {t('admin.schedules.customDaysOff.addDate')}
          </Button>
        </div>
      )}

      {dateError != null && (
        <p className="text-sm text-destructive">
          {Array.isArray(dateError) ? dateError.join(', ') : dateError}
        </p>
      )}

      {totalCount === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.schedules.customDaysOff.emptyState')}
        </p>
      ) : (
        <div className="space-y-4">
          {props.holidaysByYear.map(({ year, holidays }) => (
            <div key={year}>
              <p className="mb-2 text-sm font-semibold text-muted-foreground">{year}</p>
              <ul className="space-y-1">
                {holidays.map((holiday) => (
                  <li
                    key={holiday.id}
                    className="flex items-center justify-between rounded border px-3 py-2"
                  >
                    <span className="text-sm">
                      {holiday.date ? formatCalendarDate(holiday.date) : ''}
                    </span>
                    {props.canMutate && (
                      <RemoveHolidayDialog holidayId={holiday.id}>
                        <Button type="button" variant="ghost" size="sm">
                          {t('admin.schedules.customDaysOff.remove')}
                        </Button>
                      </RemoveHolidayDialog>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
